"use client"

import React, { useState, useEffect } from "react"
import { useRouter } from 'next/router';
import Link from "next/link"
import Image from 'next/image';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import styles from "../styles/mtn-digital-services.module.scss"
import ContactForm from '../components/ContactForm';
import { PhoneInput } from '../components/organisms/input/phone-input';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { notify } from '../utils/notifications';


export default function MTNBetaPage() {
    const { t, ready } = useTranslation();
    const [isChecked, setIsChecked] = useState(false)
    const [phoneValue, setPhoneValue] = useState("")
    const [dialingCode, setDialCode] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [serviceUrl, setServiceUrl] = useState("")
    const [countdown, setCountdown] = useState(5)
    const [showRedirectMessage, setShowRedirectMessage] = useState(false)
    const [isPhoneValid, setIsPhoneValid] = useState(false)
    const router = useRouter();
    
    useEffect(() => {
      router.push('/mtn-digital-services/cm');
    }, [router]); 
   
    if (!ready) {
        return <div>Loading translations...</div>; 
    }
    
    const handlePhoneValidityChange = (isValid) => {
        setIsPhoneValid(isValid)
        console.log('Phone is valid:', isValid)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isChecked && !phoneValue || isSubmitting)
            return;

        console.log('Submitting with phone:', isPhoneValid);

        setIsSubmitting(true);

        try {
            const response = await fetch("/api/digital-service-interaction", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mobileNumber: phoneValue,
                    dialingCode: dialingCode
                })

            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                notify(
                    'error',
                    data.error || 'Error: Oops! Something went wrong on our side. Please try again later.'
                );
                return;
            }

            const url = data.url;

            if (!url) {
                notify(
                    'error',
                    'Error: Service URL not available. Please try again later.'
                );
                return;
            }

            notify(
                'success',
                'You will be redirected to the App Store in a few seconds. If not, please click the link below.'
            );

            setServiceUrl(url);
            setShowRedirectMessage(true);
            setCountdown(5);

            const countdownInterval = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        window.location.href = url;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        } catch (error) {
            notify(
                'error',
                `Error: Oops! Something went wrong on our side. Please try again later.`
            );
            console.error('Submission error:', error);
        } finally {
            setIsSubmitting(false);
        }

    }

  return (
    <div className={styles.container}>
      {/* Hero Section with Beta Signup */}
      <section className={styles.heroSection}>
        {/* Language Selector */}
        <div className={styles.languageSelector}>
            <LanguageSwitcher />
        </div>

        {/* MTN One Logo */}
        <div className={styles.logo}>
          <div className={styles.mtnPart}>
              <Image src="/images/mtn-one-logo.png" height={77} width={280} alt="MTN One" />
          </div>
        </div>

        {/* Main Heading */}
        <h1 className={styles.mainHeading}><Trans i18nKey="mainHeading" /></h1>

        {/* Subheading */}
        <p className={styles.subheading}><Trans i18nKey="subheading" /></p>

        {/* Description */}
        <div className={styles.description}>
          <span className={styles.highlight}>
              <Trans i18nKey="description.highlight" /></span>
          <span>
              — <Trans i18nKey="description.content" />
          </span>
        </div>

        {/* Beta Form Card */}
        <div className={styles.betaCard}>
            <div className={styles.content}>
              <h2><Trans i18nKey="legalText.heading" /></h2>

              {/* <div className={styles.legalText}>
                  <Trans i18nKey="legalText.content" />
              </div> */}
               <div 
                        className={styles.legalText}
                        dangerouslySetInnerHTML={{ __html: t('legalText.content') }}
                    />

            </div> 
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Phone Number Input */}
            <div className={styles.phoneInput}>
              <div className={styles.inputWrapper}>
                {/* Dropdown to select country with emoji flags */}
                  <PhoneInput value={phoneValue} onChange={setPhoneValue} onDialCodeChange={setDialCode} onValidityChange={handlePhoneValidityChange} placeholder="00 000 0000" />
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className={styles.termsCheckbox}>
              <input type="checkbox" id="terms" checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} />
              <label htmlFor="terms">
                  <Trans i18nKey="terms.label" />{" "}
                <Link href="/mtn-terms-and-conditions">{t('terms.link1')}</Link>, {t('terms.link1_1')}{" "}
                <Link href="/mtn-privacy-policy">{t('terms.link2')}</Link>.
              </label>
            </div>

              <button
                  type="submit"
                  disabled={!isChecked && !phoneValue || isSubmitting}
                  className={styles.submitButton}
              >
                  {isSubmitting ? (
                      <>
                          <span className={styles.spinner}></span>
                          <Trans i18nKey="submitting" />
                      </>
                  ) : (
                      <Trans i18nKey="submitButton" />
                  )}
              </button>
              {showRedirectMessage && (
                  <div className={styles.redirectMessage}>
                      <p>
                          Click <Link href={serviceUrl}>here</Link> if you are not redirected in {countdown} seconds
                      </p>
                  </div>
              )}
          </form>
        </div>
      </section>

      {/* How to Install Section */}
      <section className={styles.installSection}>
        <div className={styles.container}>
          <h2><Trans i18nKey="installSection.title" /></h2>
          <p className={styles.subtitle}><Trans i18nKey="installSection.subtitle" /></p>

          <div className={styles.stepsGrid}>
            {/* Step 1 */}
            <div className={styles.step}>
              <div className={`${styles.stepCard} ${styles.black}`}>
                <div className={styles.stepNumber}>1</div>
                <Image src="/images/mtn-one-1.png" height={348} width={165} alt="MTN One Step 1" />
              </div>
              <h3><Trans i18nKey="step1.heading" /></h3>
              <p><Trans i18nKey="step1.subheading" /></p>
            </div>

            {/* Step 2 */}
            <div className={styles.step} style={{"padding-bottom": "0"}}>
              <div className={`${styles.stepCard} ${styles.yellow}`}>
                <div className={`${styles.stepNumber} ${styles.bordered}`}>2</div>
                <Image src="/images/mtn-one-2.png" height={300} width={190} alt="MTN One Step 2" />
                <div className={styles.comingSoon}>
                    <p><Trans i18nKey="step2.coming" /></p>
                </div>
              </div>
                <h3><Trans i18nKey="step2.heading" /></h3>
                <p><Trans i18nKey="step2.subheading" /></p>
            </div>

            {/* Step 3 */}
            <div className={styles.step}>
              <div className={`${styles.stepCard} ${styles.gray}`}>
                <div className={styles.stepNumber}>3</div>
                  <Image src="/images/mtn-one-3.png" height={332} width={156} alt="MTN One Step 3" />
              </div>
                <h3><Trans i18nKey="step3.heading" /></h3>
                <p><Trans i18nKey="step3.subheading" /></p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className={styles.contactSection}>
        <div className={styles.container}>
          <div className={styles.contactText}>
            <h2><Trans i18nKey="contactForm.heading" /></h2>
            <p>
                <Trans i18nKey="contactSection.description" />
            </p>
          </div>

          <div className={styles.contactForm}>
            <h3><Trans i18nKey="contactForm.heading" /></h3>
            <ContactForm />
          </div> 
        </div>  
      </section>
    </div>
  )
}

